import React from "react";
import { useSelector, useDispatch } from "react-redux";
import clsx from "clsx";

import { SUBMITTER_IS_CONTRIBUTOR_CHANGED } from "intro/introActions";

import { getStepFields, isIntroStepLoading } from "common/selectors";

import { COMPLETE_STEP } from "intro/introActions";

import * as steps from "common/steps";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";
import Button from "@material-ui/core/Button";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import LoadingPanel from "general-components/LoadingPanel";
import PersonalDetailsForm from "../../basic-details/components/PersonalDetailsForm";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },

  form: {
    height: "100%",
    maxHeight: 800,
    minHeight: 400,
  },

  formBody: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    maxHeight: 800,
    minHeight: 400,
  },

  heading: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: theme.palette.primary.dark,
  },

  subheading: {
    fontSize: "1rem",
    color: theme.palette.primary.main,
  },

  extraDirection: {
    fontSize: ".85rem",
    color: theme.palette.primary.main,
  },

  introQuestion: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(5),
  },

  question: {
    fontSize: "1rem",
    fontWeight: "bold",
  },

  radioGroup: {
    minWidth: 125,
    display: "flex",
    flexDirection: "row",
  },

  radioBox: {
    maxWidth: 150,
    minWidth: 140,
    maxHeight: 75,
    borderStyle: "solid",
    borderColor: theme.palette.primary.light,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
  },

  selectedRadioBox: {
    borderColor: theme.palette.secondary.main,
    color: theme.palette.secondary.main,
  },

  stepperNavigation: {
    flexGrowth: 1,
    display: "flex",
    justifyContent: "flex-end",
  },

  stepperNavigationRtl: {
    justifyContent: "flex-start",
  },

  nextButton: {
    fontSize: "1rem",
    fontWeight: "bold",
  },
}));

const Intro = () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();

  const fields = useSelector(getStepFields(steps.INTRO));
  const loading = useSelector(isIntroStepLoading());

  const dispatch = useDispatch();
  const onNext = () => dispatch({ type: COMPLETE_STEP });

  const handleRadioChange = (event) => {
    const val = event.target.value;
    if (val === "yes") {
      dispatch({ type: SUBMITTER_IS_CONTRIBUTOR_CHANGED, value: true });
    } else {
      dispatch({ type: SUBMITTER_IS_CONTRIBUTOR_CHANGED, value: false });
    }
  };

  return loading ? (
    <LoadingPanel />
  ) : (
    <Paper classes={{ root: classes.root }} elevation={0}>
      <div className={classes.formBody}>
        <div>
          <div>
            <div className={classes.heading}>{t("intro_greeting")}</div>
            <div className={classes.subheading}>{t("intro_greeting_sub")}</div>
            <div className={classes.extraDirection}>
              {t("intro_greeting_extra")}
            </div>
          </div>

          <div className={classes.introQuestion}>
            <div className={classes.question}>{t("intro_question")}</div>
            <div className={classes.subheading}>{t("intro_question_sub")}</div>
            <div className={classes.extraDirection}>
              {t("intro_question_extra")}
            </div>
          </div>

          <RadioGroup
            classes={{ root: classes.radioGroup }}
            aria-label="creator"
            name="creator"
            value={fields.isSubmitterCreator ? "yes" : "no"}
            onChange={handleRadioChange}
          >
            <div
              className={clsx(
                classes.radioBox,
                fields.isSubmitterCreator && classes.selectedRadioBox
              )}
            >
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label={t("intro_is_submitter_yes")}
              />
            </div>

            <div
              className={clsx(
                classes.radioBox,
                !fields.isSubmitterCreator && classes.selectedRadioBox
              )}
            >
              <FormControlLabel
                value="no"
                control={<Radio />}
                label={t("intro_is_submitter_no")}
              />
            </div>
          </RadioGroup>

          {!fields.isSubmitterCreator && (
            <div>
              <div className={classes.introQuestion}>
                <div className={classes.question}>
                  {t("intro_submitter_creator")}
                </div>
                <div className={classes.subheading}>
                  {t("intro_submitter_creator_sub")}
                </div>
              </div>
              <PersonalDetailsForm />
            </div>
          )}
        </div>

        <div
          className={clsx(classes.stepperNavigation, {
            [classes.stepperNavigationRtl]: i18n.dir() === "rtl",
          })}
        >
          <Button
            classes={{ root: classes.nextButton }}
            variant="text"
            onClick={onNext}
          >
            {t("intro_get_started")}
            {i18n.dir() === "rtl" ? <ArrowBackIcon /> : <ArrowForwardIcon />}
          </Button>
        </div>
      </div>
    </Paper>
  );
};

export default Intro;
